// ---------------------------------------------------------------------------
// Content Service - V11 Discovery API
// ---------------------------------------------------------------------------
// API client for V11 Discovery Content (Posts) endpoints
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'

export interface ContentFilters {
  readonly page?: number
  readonly limit?: number
  readonly sort?: string
  readonly order?: 'asc' | 'desc'
  readonly search?: string
  readonly date_from?: string
  readonly date_to?: string
  readonly platform?: readonly number[]
  readonly category?: readonly number[]
  readonly media_type?: readonly number[]
  readonly performance?: 'high_performing' | 'medium_performing' | 'low_performing'
}

export interface ContentResponse {
  readonly success: boolean
  readonly data: readonly Record<string, unknown>[]
  readonly meta: {
    readonly page: number
    readonly limit: number
    readonly total: number
    readonly lastPage: number
    readonly hasMorePages: boolean
  }
}

export interface ContentAggregationsResponse {
  readonly success: boolean
  readonly data: {
    readonly platforms: readonly { name: string; count: number; avgLikes: number }[]
    readonly categories: readonly { name: string; count: number }[]
    readonly mediaTypes: readonly { name: string; count: number }[]
    readonly performance: readonly { name: string; count: number }[]
    readonly video: readonly { name: string; count: number }[]
    readonly emotion: readonly { name: string; count: number }[]
    readonly authorCountries: readonly { name: string; count: number }[]
    readonly dates: readonly { name: string; count: number }[]
    readonly totals: {
      readonly posts: number
      readonly likes: number
      readonly comments: number
      readonly avgEngagement: number
      readonly avgSentiment: number
    }
  }
}

export interface ContentTrendsResponse {
  readonly success: boolean
  readonly data: {
    readonly metric: string
    readonly groupBy: string
    readonly timeline: readonly Record<string, unknown>[]
  }
}

export const contentService = {
  async getContent(filters: ContentFilters = {}): Promise<ContentResponse> {
    const response = await api.get<ContentResponse>('/api/v11/discovery/content', {
      params: filters,
    })
    return response
  },

  async getAggregations(filters: ContentFilters = {}): Promise<ContentAggregationsResponse> {
    const response = await api.get<ContentAggregationsResponse>(
      '/api/v11/discovery/content/aggregations',
      { params: filters }
    )
    return response
  },

  async getTrends(
    metric: string,
    groupBy: string,
    filters: ContentFilters = {}
  ): Promise<ContentTrendsResponse> {
    const response = await api.get<ContentTrendsResponse>(
      '/api/v11/discovery/content/trends',
      { params: { metric, group_by: groupBy, ...filters } }
    )
    return response
  },
}
